import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useSession } from '../../state/useSession';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import { Button, Screen, TextField } from '../../ui';
import { PASSWORD_RULE } from './passwordRule';
import { holdPassword } from './heldPassword';
import type { ConfirmState } from './Confirm';

/** Cognito's name for an email that already has an account, confirmed or not. */
const EXISTS = 'UsernameExistsException';

export default function SignUp() {
  const { signUp } = useSession();
  const navigate = useNavigate();
  /*
   * Where the person was going when an account was asked of them — the plan they were
   * building, an invite they opened. Carried through confirmation to the sign-in that follows
   * it, which is where they are finally returned: dropped here, "sign in to keep going"
   * delivered everybody without an account (most of whom it was said to) to the home page.
   */
  const from = (useLocation().state as { from?: string } | null)?.from;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The email has an account already: Sign in, or — if it was never confirmed — the code. */
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setExists(false);
    setBusy(true);
    try {
      /*
       * The email IS the username. The pool's UsernameAttributes is email/phone, so a
       * generated username throws InvalidParameterException — a regression this project
       * has already shipped once.
       */
      await signUp(email.trim(), password, { acceptedTerms: accepted });
      // For the sign-in the emailed code completes, so nothing is typed twice (F7).
      holdPassword(email, password);
      const state: ConfirmState = { email: email.trim(), ...(from ? { from } : {}) };
      navigate('/confirm', { state });
    } catch (err) {
      /*
       * "An account with the given email already exists." ended the funnel for somebody who had
       * signed up before and closed the tab ahead of the code (F7, 2026-09-30): the account is
       * there, unconfirmed, and nothing here led to confirming it. Both ways on are offered —
       * this page cannot tell a confirmed account from an unconfirmed one. The password is held
       * in case it is the one they chose then, so the code can sign them straight in.
       */
      if ((err as { code?: string } | null)?.code === EXISTS) {
        holdPassword(email, password);
        setExists(true);
        return;
      }
      setError(err instanceof Error ? err.message : 'Could not create your account.');
    } finally {
      setBusy(false);
    }
  }

  const onward = { email: email.trim(), ...(from ? { from } : {}) };

  return (
    <Screen title="Create account">
      <form onSubmit={submit} noValidate>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChangeText={setEmail}
          placeholder="you@example.com"
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChangeText={setPassword}
          hint={PASSWORD_RULE}
        />
        <label className="type-caption checkbox-row">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>
            I accept the <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a> and{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
          </span>
        </label>
        {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
        {exists && (
          <div role="alert" className="type-caption field-error-text auth-notice">
            <p>
              This email already has an account. <Link to="/sign-in" state={onward}>Sign in</Link>, or if you
              never entered the code we emailed, confirm it now.
            </p>
            <Button
              title="Confirm your email"
              variant="secondary"
              onPress={() => navigate('/confirm', { state: { ...onward, resend: true } satisfies ConfirmState })}
            />
          </div>
        )}
        <Button title="Create account" type="submit" loading={busy} disabled={!accepted} />
      </form>
      <p className="type-body auth-links">
        Already have an account? <Link to="/sign-in" state={from ? { from } : undefined}>Sign in</Link>
      </p>
    </Screen>
  );
}
